import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import Dataset from '../model/dataset'
const { isTotal, TOTAL } = Dataset
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'

/**
 * Returns the label used to describe an aggregation to a user.  For example,
 * "Number of sexual assaults", "Sexual assaults %", "Contract value sum".
 * Note aggregation.fieldId may be -1*datasetId to indicate the "tracked" field
 * for that dataset.
 *
 * @param {Dataset} datasetModel the Dataset model
 * @param {Object} aggregation the aggregation
 * @returns {String}
 */
function aggregationLabel(datasetModel, aggregation) {
	if (aggregation.labelType === 'custom') {
		return aggregation.label
	}

	if (aggregation.labelType === 'none') {
		return ''
	}

	if (aggregation.dataSourceType === 'calculated') {
		return aggregation.equation
	}

	var key, label
	// isTotal is cool to use here because -1*fieldId really does mean it's a
	// total field so this is the correct branch to use
	if (isTotal(aggregation.fieldId) ||
		_.isNil(aggregation.fieldId) || '' === aggregation.fieldId) {
		if (aggregation.aggregationType == 'sum') {
			key = 'datasets.total.tracked'
		} else {
			key = 'datasets.aggregation.percentage'
		}
		if (aggregation.visibleLinkedFieldId) {
			let datasetId = datasetModel.getDatasetByVisualLinkedFieldId(aggregation.visibleLinkedFieldId).id
			if (datasetId == datasetModel.get('rootDatasetId')) {
				label = datasetModel.get('tracked')
			} else {
				label = datasetModel.visibleLinkedFieldsById[aggregation.visibleLinkedFieldId].label
				if (aggregation.aggregationType == 'sum') {
					return label
				} else {
					return i18n.get('datasets.aggregation.percentage', label)
				}
			}
		} else if (_.isEmpty(aggregation.fieldId) ||
			// note the fieldId could be -1*datasetId, so don't use the isTotal
			// function because if it's a linked dataset we want to fall into
			// the else clause below
			aggregation.fieldId == TOTAL ||
			aggregation.fieldId == -1 * datasetModel.get('rootDatasetId')) {
			label = datasetModel.get('tracked')
		} else {
			let datasetId = -1 * aggregation.fieldId
			label = datasetModel.datasetsById[datasetId].get('tracked')
		}
	} else {
		key = getKeyForAggregationAndField(aggregation, datasetModel)
		let fieldIdentifier = datasetModel.idsToFieldIdentifier(aggregation)
		label = datasetModel.getFieldLabel(fieldIdentifier)
	}
	return i18n.get(key, label)
}

function getKeyForAggregationTypeAndDataType(aggregationType, dataType) {
	// Date/Time aggregations are a unlike other non-totals in that the type
	// of the aggregation may differ from the type of the field. e.x. Max time
	// in a datetime field. This if block is to handle that.
	if (isDateTimeDataType(dataType)) {
		switch (aggregationType) {
			case 'min-date': return 'datasets.aggregation.minimum.date'
			case 'max-date': return 'datasets.aggregation.maximum.date'
			case 'min-time': return 'datasets.aggregation.minimum.time'
			case 'max-time': return 'datasets.aggregation.maximum.time'
		}
		// Aggregation data type matches field data type. Repeating it in the
		// label would be redundant.
		if (_.includes(['minimum', 'maximum'], aggregationType)) {
			return `datasets.aggregation.${aggregationType}.dateTime`
		}
	}

	return 'datasets.aggregation.' + _.camelCase(aggregationType)
}

function getKeyForAggregationAndField(aggregation, datasetModel) {
	const aggregationType = aggregation.aggregationType
	const dataType = datasetModel.combinedFieldsById[aggregation.fieldId].dataType

	return getKeyForAggregationTypeAndDataType(aggregationType, dataType)
}

export default aggregationLabel
export { getKeyForAggregationTypeAndDataType }

aggregationLabel.__filename__ = 'datasets/overview/aggregation-label.js'
